<template>
  <BaseTable
    :isLoading="isLoading"
    :tableData="data"
    :totalItemsCount="totalItemsCount"
    :perPage="perPage"
    :currentPage="currentPage"
    :showSearch="true"
    searchPlaceholder="Search by name, account group, customer id"
    :showCreateButton="true"
    createLabel="Add CannedResponses"
    :showSlot="showEditor"
    @create="displayCreate"
    @search="onSearch"
    @clear="onClear"
    @paginate="paginate"
  >
    <template #columns>
      <Column field="id" header="Id" :styles="{ 'min-width': '10rem' }">
        <template #body="{ data }">
          <p>{{ data.id }}</p>
        </template>
      </Column>
      <Column field="name" header="Name" :styles="{ 'min-width': '10rem' }">
        <template #body="{ data }">
          <p>{{ data.name }}</p>
        </template>
      </Column>
      <Column field="status" header="Status" :styles="{ 'min-width': '10rem' }">
        <template #body="{ data }">
          <p>{{ data.status }}</p>
        </template>
      </Column>
    </template>

    <template #actions-column>
      <Column header="Actions">
        <template #body="{ data }">
          <div class="data-table__actions">
            <span
              v-if="loggedUser && (
                loggedUser.role === 'company_admin' ||
                loggedUser.role === 'global_admin' ||
                loggedUser.role === 'department_admin'
              )"
              @click="editionModal(data)"
            >
              <i class="pi pi-pencil"></i>
            </span>
            <span
              v-if="loggedUser && (
                loggedUser.role === 'company_admin' ||
                loggedUser.role === 'global_admin' ||
                loggedUser.role === 'department_admin'
              )"
              @click="deleteRow(data)"
            >
              <i class="pi pi-trash"></i>
            </span>
          </div>
        </template>
      </Column>
    </template>

    <template #alt-view>
      <div v-if="showEditor" class="canned-responses__content__editor">
        <Button
          icon="pi pi-arrow-left"
          class="p-button-rounded p-button-outlined canned-responses__content__button"
          @click="backToCards"
        />
        <ResponsesEditor
          :rowDataCanned="currentRowDataCanned"
          :currentCompanyDepartment="currentCompanyDepartment"
          :typeCrud="typeCrud"
          @updateCannedResponsesData="updateCannedResponsesData"
        />
      </div>
    </template>
  </BaseTable>
</template>

<script>
import { mapGetters } from "vuex";
import { successMessage, deleteMessage } from "@/utils/alertMessages";
import ResponsesEditor from "@/views/cannedResponses/components/Editor.vue";
import BaseTable from "./BaseTable.vue";

export default {
  name: "CannedResponsesTable",
  components: { BaseTable, ResponsesEditor },
  data() {
    return {
      isLoading: false,
      data: [],
      loggedUser: null,
      profileActive: null,
      currentCompanyDepartment: null,
      totalItemsCount: 0,
      currentPage: 1,
      perPage: 15,
      showEditor: false,
      typeCrud: null,
      currentRowDataCanned: null,
    };
  },
  computed: {
    ...mapGetters("cannedResponses", ["showAllCannedResponses"]),
    ...mapGetters("user", ["showCurrentCompanyDepartment"]),
  },
  watch: {
    showAllCannedResponses() {
      if (this.showAllCannedResponses) {
        this.data = this.showAllCannedResponses?.data;
        this.totalItemsCount = this.showAllCannedResponses?.count;
      }
    },
    async showCurrentCompanyDepartment() {
      if (this.showCurrentCompanyDepartment) {
        this.currentCompanyDepartment = this.showCurrentCompanyDepartment;
        this.currentPage = 1;
        this.backToCards();
        await this.getInfoProfile();
        await this.getAllCannedResponses();
      }
    },
  },
  async created() {
    this.loggedUser = JSON.parse(localStorage.getItem("user-info"));
    this.currentCompanyDepartment = this.showCurrentCompanyDepartment;
    await this.getInfoProfile();
    await this.getAllCannedResponses();
    this.data = this.showAllCannedResponses?.data;
    this.totalItemsCount = this.showAllCannedResponses?.count;
  },
  methods: {
    async getInfoProfile() {
      this.isLoading = true;
      const payload = {
        company_id: this.currentCompanyDepartment.chosenCompany.id,
        department_id: this.currentCompanyDepartment.chosenDepartment.id,
      };
      const response = await this.$store.dispatch("company/getProfileCompanysDepartment", payload);
      this.profileActive = response.status === 200 ? response.data : null;
      this.isLoading = false;
    },
    async getAllCannedResponses(params) {
      this.isLoading = true;
      const payload = { limit: 15, skip: (this.currentPage - 1) * 15 };
      if (params?.name) payload.name = params.name;
      if (this.loggedUser.role === "company_admin") {
        payload.company_id = this.currentCompanyDepartment.chosenCompany.id;
        payload.department_id = -2;
      } else {
        payload.company_id = this.currentCompanyDepartment.chosenCompany.id;
        payload.department_id = this.currentCompanyDepartment.chosenDepartment.id;
      }
      await this.$store.dispatch("cannedResponses/getAllCannedResponses", payload);
      this.isLoading = false;
    },
    async getCannedResponseById(id) {
      const params = {
        company_id: this.currentCompanyDepartment.chosenCompany.id,
        department_id: this.currentCompanyDepartment.chosenDepartment.id,
        canned_response_id: id,
      };
      const response = await this.$store.dispatch("cannedResponses/getCannedResponseById", params);
      this.currentRowDataCanned = response.data;
    },
    displayCreate() {
      this.showEditor = true;
      this.typeCrud = "create";
    },
    backToCards() {
      this.showEditor = false;
    },
    async editionModal(rowData) {
      this.isLoading = true;
      await this.getCannedResponseById(rowData.id);
      this.isLoading = false;
      this.showEditor = true;
      this.typeCrud = "edit";
    },
    async onSearch(term) {
      if (term) {
        this.currentPage = 1;
        await this.getAllCannedResponses({ name: term });
      }
    },
    async onClear() {
      this.currentPage = 1;
      await this.getAllCannedResponses();
    },
    async paginate(page) {
      this.currentPage = page;
      await this.getAllCannedResponses();
      this.getInfoProfile();
      window.scrollTo(0, 0);
    },
    async updateCannedResponsesData() {
      this.showEditor = false;
      await this.getAllCannedResponses();
    },
    async deleteRow(rowData) {
      const msg = "Are you sure you want to remove CannedResponses from the system?";
      const warningResponse = await deleteMessage(msg, "You won't be able to revert this!");
      if (warningResponse.isConfirmed) {
        const info = {
          company_id: this.currentCompanyDepartment.chosenCompany.id,
          department_id: this.currentCompanyDepartment.chosenDepartment.id,
          canned_response_id: rowData.id,
          confirmation_message: "I'm sure I want to delete this",
        };
        const response = await this.$store.dispatch("cannedResponses/deleteCannedResponse", info);
        if (response.status === 204) {
          successMessage("Canned responses has been deleted");
          if (this.data.length === 1 && this.currentPage > 1) this.currentPage -= 1;
          await this.getAllCannedResponses();
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@include table-action-icons;

.canned-responses__content {
  &__editor {
    display: flex;
    justify-content: space-between;
    width: 85%;
  }
  &__button {
    @include button-outlined;
    margin-top: 1rem;
  }
}

@media (max-width: 660px) {
  .canned-responses__content__editor {
    flex-direction: column;
  }
}
</style>
